import axios from 'axios';

// Create base axios instance for API calls
// In development, use the local server, in production use the Cloudflare Workers backend
let API_URL;
if (process.env.NODE_ENV === 'production') {
  API_URL = process.env.REACT_APP_API_URL || 'https://wastenot-api.tinkacode.workers.dev/api';
} else {
  // For local development, prefer the deployed worker URL for image compatibility
  // Use environment variable if set, otherwise default to the deployed worker
  API_URL = process.env.REACT_APP_LOCAL_API_URL || 'https://wastenot-api.tinkacode.workers.dev/api';
}

console.log('%c[WasteNot API]', 'background: #295; color: white; padding: 2px 4px; border-radius: 3px;', 'API is configured to use:', API_URL);

// Check if the API service is available
const checkApiService = async () => {
  try {
    const response = await fetch(`${API_URL}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });
    
    if (response.ok) {
      console.log('%c[WasteNot API]', 'background: #295; color: white; padding: 2px 4px; border-radius: 3px;', 'API service is available ✅');
    } else {
      console.warn('%c[WasteNot API]', 'background: #f80; color: white; padding: 2px 4px; border-radius: 3px;', 'API service returned error:', response.status);
    }
  } catch (error) {
    console.error('%c[WasteNot API]', 'background: #e55; color: white; padding: 2px 4px; border-radius: 3px;', 'API service is not reachable:', error);
  }
};

// Check API availability
checkApiService();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable credentials to include cookies in cross-origin requests
  withCredentials: true,
});

// Request interceptor for adding the auth token
api.interceptors.request.use(
  (config) => {
    // Create group for this API request in console
    console.group(`%c[API Request] ${config.method.toUpperCase()} ${config.url}`, 'color: #0066cc; font-weight: bold;');
    console.log('Request time:', new Date().toISOString());
    console.log('Request URL:', `${config.baseURL}${config.url}`);
    console.log('Request method:', config.method.toUpperCase());
    console.log('Request params:', config.params || 'none');
    console.log('Request data:', config.data || 'none');
    
    const token = localStorage.getItem('wastenet_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Auth token:', `Bearer ${token.substring(0, 10)}...`);
    } else {
      console.warn('⚠️ No auth token available for request!');
    }
    
    // Get current user data to debug role issues
    try {
      const userData = localStorage.getItem('wastenet_user');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User data:', { id: user.id, name: user.name, role: user.role });
      } else {
        console.warn('⚠️ No user data found in localStorage');
      }
    } catch (err) {
      console.error('Error parsing user data:', err);
    }
    
    console.groupEnd();
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for handling responses and errors
api.interceptors.response.use(
  (response) => {
    // Create group for this API response in console
    console.group(`%c[API Response] ${response.config.method.toUpperCase()} ${response.config.url}`, 'color: #00aa55; font-weight: bold;');
    console.log('Response time:', new Date().toISOString());
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    
    // Log response data, handling JSON strings properly
    try {
      const parsedData = { ...response.data };
      
      // Try to parse any JSON strings for better visibility
      if (parsedData.listings && Array.isArray(parsedData.listings)) {
        parsedData.listings = parsedData.listings.map(listing => {
          try {
            // Check if images field is a JSON string and parse it
            if (typeof listing.images === 'string') {
              listing.images = JSON.parse(listing.images);
              console.log('Parsed images from JSON string:', listing.images);
            }
            // Check if location field is a JSON string and parse it
            if (typeof listing.location === 'string') {
              listing.location = JSON.parse(listing.location);
              console.log('Parsed location from JSON string:', listing.location);
            }
          } catch (e) {
            console.warn('Failed to parse JSON string in listing:', e);
          }
          return listing;
        });
      }
      
      console.log('Response data:', parsedData);
    } catch (e) {
      console.log('Response data:', response.data);
      console.warn('Error parsing response data for logging:', e);
    }
    
    console.groupEnd();
    return response;
  },
  (error) => {
    // Create group for error response
    console.group(`%c[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`, 'color: #cc0000; font-weight: bold;');
    console.log('Error time:', new Date().toISOString());
    
    if (error.response) {
      // The request was made and the server responded with an error status
      console.log('Error status:', error.response.status);
      console.log('Error headers:', error.response.headers);
      console.log('Error data:', error.response.data);
      
      // Handle 401 (Unauthorized) by redirecting to login
      if (error.response.status === 401) {
        console.warn('Authentication error, redirecting to login...');
        localStorage.removeItem('wastenet_token');
        localStorage.removeItem('wastenet_user');
        // If not already on /login, redirect to it
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (error.response.status === 404) {
        console.warn('Resource not found (404). Check API URL and endpoints.');
      } else if (error.response.status === 403) {
        console.warn('Access forbidden (403). Check user permissions.');
      } else if (error.response.status >= 500) {
        console.error('Server error:', error.response.status);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      console.warn('The API server might be down. Check server availability.');
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
    }
    
    console.groupEnd();
    return Promise.reject(error);
  }
);

export default api;
